import { useCallback } from 'react';
import { useAuthStore } from '@/store/authStore';
import { api } from '@/services/apiClient';
import type { AuthenticatedUser, LoginRequest, LoginResponse } from '@optiflow/types';

export interface UseAuthResult {
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  isRestoring: boolean;
  login: (credentials: LoginRequest) => Promise<AuthenticatedUser>;
  logout: () => Promise<void>;
}

export function useAuth(): UseAuthResult {
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const setSession = useAuthStore((state) => state.setSession);
  const clear = useAuthStore((state) => state.clear);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      const result = await api.post<LoginResponse>('/auth/login', credentials);
      setSession(result.user, result.tokens);
      return result.user;
    },
    [setSession],
  );

  const logout = useCallback(async () => {
    const { refreshToken } = useAuthStore.getState();

    if (refreshToken) {
      // A failed revocation must not trap the user in a signed-in shell, so the error is swallowed.
      await api.post('/auth/logout', { refreshToken }).catch(() => undefined);
    }

    clear();
  }, [clear]);

  return {
    user,
    isAuthenticated: status === 'authenticated' && user !== null,
    isRestoring: status === 'restoring',
    login,
    logout,
  };
}
